import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AvailableDomain} from '../../../../../models/cms/available-domain';
import {AvailableDomainService} from '../../../../../services/cms/available-domain.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {IsGranted} from '../../../../../decorator/is-granted';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {NgOptimizedImage} from '@angular/common';
import {Language} from '../../../../../models/language';
import {Product} from '../../../../../models/product';
import {forkJoin} from 'rxjs';
import {LangService} from '../../../../../services/language/lang.service';
import {ProductService} from '../../../../../services/product/product.service';

@Component({
  selector: 'app-available-domain-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage
  ],
  templateUrl: './available-domain-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './available-domain-form.component.scss'
})
@IsGranted(['STATIC_PAGE_VIEW'])
@MetaTitle('Domain Configuration Form')
export class AvailableDomainFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  availableDomain: AvailableDomain | null = null;
  products: Product[] = [];
  languages: Language[] = [];

  form = new FormGroup({
    domain: new FormControl('', Validators.required),
    slug: new FormControl<string | null>(null),
    productId: new FormControl<number | null>(null),
    lang: new FormControl<string | null>(null),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private availableDomainService: AvailableDomainService,
    private productService: ProductService,
    protected langService: LangService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();

    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.langService.getLanguages()
    ]).subscribe(([products, languages]) => {
      this.products = products;
      this.languages = languages;

      if (this.id) {
        this.availableDomainService.findOneById(this.id).subscribe((availableDomain: AvailableDomain) => {
          this.availableDomain = availableDomain;

          this.form.patchValue({
            domain: this.availableDomain.domain,
            slug: this.availableDomain.slug,
            productId: this.availableDomain.product?.id ?? null,
            lang: this.availableDomain.lang,
          });

          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    if (this.isCreate()) {
      this.availableDomainService.create(this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('configuration/domain').then(() => {
          this.snackbar.success('The domain has been added');
        });
      });
    } else if (this.availableDomain?.id) {
      this.availableDomainService.update(this.availableDomain.id, this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('configuration/domain').then(() => {
          this.snackbar.success('The domain has been modified.');
        });
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }
}
